import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    PhotoCamera as PhotoCameraIcon,
    Delete as DeleteIcon,
    Description as DescriptionIcon,
    AttachFile as AttachFileIcon,
    Link as LinkIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JHA_STORE, STATUS_NEW, STATUS_PROCESS, CSEP_STORE, LTT_STORE, FALL_ARREST_RESCUE_PLAN_STORE } from '../../../config/constants';
import { ensureAllStepsStructure, isLocationMode } from '../utils/stepTwoUtils';
import { createJhaSkeleton } from '../utils/jhaRecordFactory';
import JhaStepHeader from '../JhaStepHeader';
import JhaInitialSubmission from '../JhaInitialSubmission';
import PdfPreviewModal from '../pdf/PdfPreviewModal';
import { generateLinkedFormPdfBlob } from '../pdf/generateJhaPdf';
import CameraCapture from '../../shared/CameraCapture';
import CsepSelectionModal from '../CsepSelectionModal';
import LttSelectionModal from '../LttSelectionModal';
import FarpSelectionModal from '../FarpSelectionModal';
import fallArrestRescuePlanService from '../../../services/fallArrestRescuePlanService';
import { generateUUID } from '../../../utils/uuid';
import { createCsepSkeleton } from '../../csep/utils/csepRecordFactory';
import { createLttSkeleton } from '../../ltt/utils/lttRecordFactory';
import { loadTriggers, evaluateTriggers, describeMatch, findMissingDocumentsLocal } from '../utils/documentTriggerEvaluator';
import { syncDataGeneric } from '../../../services/syncService';
import Swal from 'sweetalert2';
import { localDateString, localDateTimeString } from '../../../utils/localDate';

const TODAY_STRING = localDateString();

// Имя FARP-спец-документа, который автоматически добавляется в customDocsPermits
// при сматченном FARP-триггере, если его ещё нет в списке (т.к. в docs_permits
// для FARP записи нет — он подаётся отдельной формой).
const SPECIAL_DOC_NAMES = {
    fall_arrest_rescue_plan: 'Fall Arrest Rescue Plan',
    lift_evaluation: 'Lift Evaluation Form (LEF)',
};

const normLower = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '');

function hasAttachmentOrLink(doc) {
    if (Array.isArray(doc?.attachments) && doc.attachments.length > 0) return true;
    if (doc?.linkedCsepUuid || doc?.linkedLttUuid || doc?.linkedFarpUuid) return true;
    return false;
}

function buildAttachmentPayload(payload) {
    if (!payload) {
        return null;
    }

    const { file, dataUrl, name } = payload;
    const fileName = name || file?.name || `attachment_${Date.now()}.jpg`;

    return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: fileName,
        mimeType: file?.type || 'image/jpeg',
        dataUrl: dataUrl || '',
        createdAt: new Date().toISOString()
    };
}

export default function JhaStepTwo() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const { jha = null, isNew = false, viewMode = false, fromViewer = false } = state || {};

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [jhaData, setJhaData] = useState(null);
    const [otherDoc, setOtherDoc] = useState('');
    const [cameraTarget, setCameraTarget] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [csepModalOpen, setCsepModalOpen] = useState(false);
    const [csepModalTarget, setCsepModalTarget] = useState(null);
    const [lttModalOpen, setLttModalOpen] = useState(false);
    const [lttModalTarget, setLttModalTarget] = useState(null);
    const [farpModalOpen, setFarpModalOpen] = useState(false);
    const [farpModalTarget, setFarpModalTarget] = useState(null);
    const [triggerMatches, setTriggerMatches] = useState([]);
    const [missingDialog, setMissingDialog] = useState(null);
    // Per-document linked-form PDF preview (FARP/CSEP/LTT viewed individually).
    const [formPdfOpen, setFormPdfOpen] = useState(false);
    const [formPdfBlob, setFormPdfBlob] = useState(null);
    const [formPdfLoading, setFormPdfLoading] = useState(false);

    const saveTimeoutRef = useRef(null);
    const pendingRecordRef = useRef(null);
    const createdRecordIdRef = useRef(jha?.id || null);

    const isSubmitted = jhaData && (jhaData.status === 'finish' || jhaData.status === 'completed' || jhaData.syncStatus === 'synced');
    const isReadOnly = isSubmitted || viewMode;
    // Documents is now step 3 (steps swapped). The Initial JHA is sent from THIS step,
    // so "Continue to Crew" is gated on the initial submission being sent/pending.
    const initialSubmissionStatus = jhaData?.stepThree?.initialSubmission?.status;
    const initialSent = initialSubmissionStatus === 'sent' || initialSubmissionStatus === 'pending';

    useEffect(() => {
        loadData();
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            // Дописываем отложенную запись вместо того чтобы её выбросить —
            // иначе при быстром переходе на след. шаг pending-изменения (вложение,
            // toggle Yes/No Req) теряются и не доезжают в IndexedDB.
            flushSave();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        const db = await initializeDB();

        const [allDocs, allEvaluation] = await Promise.all([
            db.transaction('docsPermit', 'readonly').objectStore('docsPermit').getAll(),
            db.transaction('evaluationOfWork', 'readonly').objectStore('evaluationOfWork').getAll()
        ]);

        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        let currentRecord = null;

        if (createdRecordIdRef.current) {
            currentRecord = await store.get(createdRecordIdRef.current);
        }

        if (!currentRecord && jha?.id) {
            currentRecord = await store.get(jha.id);
        }

        if (!currentRecord && isNew) {
            const newId = generateUUID();
            currentRecord = createJhaSkeleton({
                id: newId,
                status: STATUS_NEW,
                createdAt: new Date().toISOString(),
                docs: allDocs,
                evaluation: allEvaluation,
                today: TODAY_STRING,
                metaLastStep: 2,
            });
            await store.put(currentRecord);
            createdRecordIdRef.current = currentRecord.id;
        }

        if (!currentRecord && jha) {
            const skeleton = createJhaSkeleton({
                id: jha.id,
                status: jha.status || STATUS_NEW,
                createdAt: jha.createdAt || new Date().toISOString(),
                docs: allDocs,
                evaluation: allEvaluation,
                today: TODAY_STRING,
                metaLastStep: 2,
            });

            currentRecord = {
                ...skeleton,
                ...jha,
                stepOne: { ...skeleton.stepOne, ...(jha.stepOne || {}) },
                stepTwo: { ...skeleton.stepTwo, ...(jha.stepTwo || {}) },
                stepThree: { ...skeleton.stepThree, ...(jha.stepThree || {}) },
                stepFour: { ...skeleton.stepFour, ...(jha.stepFour || {}) },
                meta: { ...skeleton.meta, ...(jha.meta || {}) },
            };
            await store.put(currentRecord);
            createdRecordIdRef.current = currentRecord.id;
        }

        if (!currentRecord) {
            setLoading(false);
            navigate('/jha');
            return;
        }

        ensureAllStepsStructure(currentRecord, allDocs, null, '', TODAY_STRING);
        setJhaData(currentRecord);
        setLoading(false);
    }, [isNew, jha, navigate]);

    const flushSave = useCallback(async () => {
        if (!pendingRecordRef.current) {
            setSaving(false);
            return;
        }

        const recordToSave = pendingRecordRef.current;
        pendingRecordRef.current = null;

        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        await store.put(recordToSave);
        await tx.done;
        setSaving(false);
        window.dispatchEvent(new Event('jhaUpdated'));
    }, []);

    const scheduleSave = useCallback((record, options = {}) => {
        const { immediate = false, minStep = 2 } = options;
        const currentLast = jhaData?.meta?.lastStep || 1;
        const recordLast = record?.meta?.lastStep || currentLast;
        const nextLast = Math.max(currentLast, recordLast, minStep);

        const recordWithMeta = {
            ...record,
            meta: { ...(record.meta || {}), lastStep: nextLast }
        };

        if (isReadOnly) {
            setJhaData(recordWithMeta);
            return;
        }

        setJhaData(recordWithMeta);
        pendingRecordRef.current = recordWithMeta;
        setSaving(true);

        if (immediate) {
            flushSave();
            return;
        }

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            flushSave();
        }, 400);
    }, [flushSave, isReadOnly, jhaData]);

    // ─── Принудительный рефреш триггеров с бэкенда при открытии JHA (онлайн) ─────
    // Корень бага в поле: на устройстве в IndexedDB триггеры устаревали/были
    // неполными → локальный оценщик НЕ видел, что FARP обязателен (хотя бэкенд
    // видел) → строка FARP не добавлялась, а submit ловил 422 без возможности
    // прикрепить. Чтобы локальная оценка совпадала с сервером, при открытии JHA
    // (онлайн) тянем свежие триггеры ПОЛНЫМ рефрешем и сразу пере-вычисляем.
    // Один раз на открытый JHA. Офлайн — используем кэш (плюс 422-инъекция как сеть).
    const triggersRefreshedForRef = useRef(null);
    useEffect(() => {
        if (!jhaData?.id || isReadOnly) return;
        if (typeof navigator !== 'undefined' && !navigator.onLine) return;
        if (triggersRefreshedForRef.current === jhaData.id) return;
        triggersRefreshedForRef.current = jhaData.id;
        (async () => {
            try {
                // full refresh: очищаем метку времени → syncHandler сделает full sync
                try { localStorage.removeItem('lastJhaDocumentTriggersSyncTime'); } catch (_) {}
                // silent: фоновый рефреш, тост прогресса юзеру не показываем
                await syncDataGeneric('jhaDocumentTriggers', { silent: true });
                const triggers = await loadTriggers();
                const matched = evaluateTriggers(triggers, jhaData.stepThree || {}, jhaData.stepOne || {});
                setTriggerMatches(matched);
                ensureSpecialPlaceholders(matched);
            } catch (e) {
                console.warn('[JhaStepTwo] trigger force-refresh failed (using cached):', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jhaData?.id, isReadOnly]);

    // Загружаем триггеры и вычисляем сматченные документы. Логика повторяет
    // backend JhaDocumentTriggerEvaluator (через сматченный набор Work Activity /
    // Energy Source / Hazard из текущей JHA). После авто-расчёта — добавляем
    // FARP-плейсхолдер в customDocsPermits, если триггер FARP сработал, а
    // соответствующего документа ещё нет в списке.
    useEffect(() => {
        if (!jhaData) return;
        (async () => {
            try {
                const triggers = await loadTriggers();
                const matched = evaluateTriggers(triggers, jhaData.stepThree || {}, jhaData.stepOne || {});
                setTriggerMatches(matched);
                ensureSpecialPlaceholders(matched);
            } catch (e) {
                console.error('[JhaStepTwo] failed to evaluate triggers', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jhaData?.id, jhaData?.stepThree]);

    // Гарантируем что любой сматченный триггером документ присутствует в списке
    // step2 — иначе badge "Triggered by" некуда прицепить. Спец-доки (FARP) и любые
    // стандартные доки из триггера, которых нет в master-списке этой JHA, кладём
    // в customDocsPermits с required='Yes Req'. Стандартные доки, уже присутствующие
    // в stepTwo.docsPermits, не дублируем — их required перебивает autoRequiredMap
    // в decorate-функции ниже.
    const ensureSpecialPlaceholders = useCallback((matched) => {
        if (!jhaData || isReadOnly) return;
        if (!matched.length) return;

        const existingBase = jhaData.stepTwo?.docsPermits || [];
        const existingCustom = jhaData.stepTwo?.customDocsPermits || [];
        const existingNames = new Set([
            ...existingBase.map(d => normLower(d.value)),
            ...existingCustom.map(d => normLower(d.value)),
        ]);

        const toAdd = matched
            .map(m => m.documentName)
            .filter(name => name && !existingNames.has(normLower(name)));

        if (!toAdd.length) return;

        // Уникализируем (один триггер может матчиться несколько раз по разным conditions)
        const uniq = [...new Set(toAdd.map(n => n.trim()))].filter(Boolean);

        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        updated.stepTwo.customDocsPermits = [
            ...existingCustom,
            ...uniq.map(name => ({ value: name, required: 'Yes Req', attachments: [] })),
        ];
        if (updated.status === STATUS_NEW) updated.status = STATUS_PROCESS;
        scheduleSave(updated, { immediate: true, minStep: 2 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jhaData, isReadOnly, scheduleSave]);

    // Карта auto-required: documentName(lower) → matchEntry
    const autoRequiredMap = useMemo(() => {
        const m = new Map();
        triggerMatches.forEach(match => {
            const docName = match.documentName;
            if (docName) m.set(normLower(docName), match);
        });
        return m;
    }, [triggerMatches]);

    const docsWithCustom = useMemo(() => {
        if (!jhaData?.stepTwo) return [];
        const base = jhaData.stepTwo.docsPermits || [];
        const custom = jhaData.stepTwo.customDocsPermits || [];
        const decorate = (doc, isCustom) => {
            const match = autoRequiredMap.get(normLower(doc.value));
            return {
                ...doc,
                isCustom,
                __autoRequired: !!match,
                __matchInfo: match || null,
                required: match ? 'Yes Req' : doc.required,
            };
        };
        return [
            ...base.map(d => decorate(d, false)),
            ...custom.map(d => decorate(d, true)),
        ];
    }, [jhaData, autoRequiredMap]);

    const handleRequirementChange = (docValue, required, isCustom = false) => {
        if (!jhaData || isReadOnly) return;
        // Auto-required документ снять нельзя — он required триггером.
        if (required !== 'Yes Req' && autoRequiredMap.has(normLower(docValue))) {
            return;
        }
        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';
        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== docValue) return item;
            const next = { ...item, required };
            if (required === 'Not Req') {
                next.attachments = [];
            }
            return next;
        });
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { minStep: 2 });
    };

    const handleAddCustomDoc = () => {
        if (!jhaData || isReadOnly) return;
        const name = otherDoc.trim();
        if (!name) return;
        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        const existing = updated.stepTwo.customDocsPermits || [];
        const exists = existing.some(doc => doc.value.toLowerCase() === name.toLowerCase());
        if (exists) {
            setOtherDoc('');
            return;
        }
        updated.stepTwo.customDocsPermits = [
            ...existing,
            { value: name, required: 'Yes Req', attachments: [] }
        ];
        setOtherDoc('');
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { immediate: true, minStep: 2 });
    };

    const handleRemoveCustomDoc = value => {
        if (!jhaData || isReadOnly) return;
        // Если документ авто-помечен триггером — удалять нельзя. Триггер всё
        // равно добавит его обратно при следующем рендере, плюс юзер получит
        // 422 на submit. Просто блокируем.
        if (autoRequiredMap.has(normLower(value))) {
            return;
        }
        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        updated.stepTwo.customDocsPermits = (updated.stepTwo.customDocsPermits || []).filter(doc => doc.value !== value);
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { immediate: true, minStep: 2 });
    };

    // FARP — особый кейс: помимо фото юзер может открыть полноценную форму FARP.
    // Распознаём по имени (трим/case-insensitive).
    const isFarpDoc = (doc) => {
        const v = normLower(doc?.value || '');
        return v === 'fall arrest rescue plan' || v.includes('fall arrest') || v.includes('rescue plan');
    };

    // Lift Evaluation Form — в справочнике документ называется «Critical Lift»,
    // им же бригады подписывали фото до появления формы.
    const isLiftEvalDoc = (doc) => {
        const v = normLower(doc?.value || '');
        return v.includes('critical lift') || v.includes('lift evaluation');
    };

    // Универсально: является ли документ одной из заполняемых форм (FARP/CSEP/LTT/LEF).
    const docFormType = (doc) => {
        if (isFarpDoc(doc)) return 'farp';
        if (isLiftEvalDoc(doc)) return 'lef';
        if (doc?.specialType === 'csep') return 'csep';
        if (doc?.specialType === 'ltt') return 'ltt';
        return null;
    };

    /**
     * Открыть Lift Evaluation Form с подставленными джобом, датой и привязкой к
     * этому JHA. По jhaLocalId JHA потом поймёт, что документ закрыт именно
     * этой формой, а не чужой за тот же день.
     */
    const openLiftEvaluationForm = () => {
        if (!jhaData) return;
        const params = new URLSearchParams();
        if (jhaData?.stepOne?.jobNumber) params.set('jobCode', jhaData.stepOne.jobNumber);
        if (jhaData?.stepOne?.date) params.set('liftDate', jhaData.stepOne.date);
        if (jhaData?.localId) params.set('jhaLocalId', jhaData.localId);
        params.set('returnTo', jhaData?.id ? `/jha/step-3?id=${encodeURIComponent(jhaData.id)}` : '/jha');
        navigate(`/operations/lift-evaluation/form?${params.toString()}`);
    };
    // Привязана ли цифровая форма (любого типа) к документу.
    const docIsLinked = (doc) => Boolean(doc?.linkedFarpUuid || doc?.linkedCsepUuid || doc?.linkedLttUuid);
    // Открыть нужную модалку формы по типу документа.
    const openFormForDoc = (doc) => {
        const target = { value: doc.value, isCustom: doc.isCustom };
        const t = docFormType(doc);
        if (t === 'farp') { setFarpModalTarget(target); setFarpModalOpen(true); }
        else if (t === 'lef') { openLiftEvaluationForm(); }
        else if (t === 'csep') { setCsepModalTarget(target); setCsepModalOpen(true); }
        else if (t === 'ltt') { setLttModalTarget(target); setLttModalOpen(true); }
    };

    // Просмотр привязанной формы (FARP/CSEP/LTT) ОТДЕЛЬНО, прямо из списка
    // документов. Запись формы всегда лежит в локальной IDB этого устройства —
    // привязка ставится только при подаче/выборе формы на нём же. Рендерим её
    // собственный PDF офлайн тем же движком, что и JHA. type: 'farp'|'csep'|'ltt'.
    const handleViewLinkedForm = async (type, uuid) => {
        if (!uuid) return;
        setFormPdfOpen(true);
        setFormPdfBlob(null);
        setFormPdfLoading(true);
        try {
            const storeMap = {
                farp: FALL_ARREST_RESCUE_PLAN_STORE,
                csep: CSEP_STORE,
                ltt: LTT_STORE,
            };
            const db = await initializeDB();
            const store = storeMap[type];
            const data = store && db.objectStoreNames.contains(store)
                ? await db.get(store, uuid)
                : null;
            const blob = data ? await generateLinkedFormPdfBlob(type, data) : null;
            if (!blob) {
                throw new Error('linked form record or renderer missing');
            }
            setFormPdfBlob(blob);
        } catch (e) {
            console.error('[JhaStepTwo] view linked form failed', e);
            setFormPdfOpen(false);
            Swal.fire('Error', 'Failed to render the document preview. Please try again.', 'error');
        } finally {
            setFormPdfLoading(false);
        }
    };

    const openFarpFormFromStep2 = () => {
        if (!jhaData) return;
        const params = new URLSearchParams();
        if (jhaData?.stepOne?.jobNumber) params.set('jobCode', jhaData.stepOne.jobNumber);
        if (jhaData?.stepOne?.date) params.set('planDate', jhaData.stepOne.date);
        if (jhaData?.localId) params.set('jhaLocalId', jhaData.localId);
        // returnTo — обратно на шаг Documents этого JHA (теперь step-3)
        let jhaPath;
        if (jhaData?.id) {
            jhaPath = `/jha/step-3?id=${encodeURIComponent(jhaData.id)}`;
        } else {
            jhaPath = '/jha';
        }
        params.set('returnTo', jhaPath);
        navigate(`/safety/forms/fall-arrest-rescue-plan/form?${params.toString()}`);
    };

    // CSEP Modal Handlers
    const handleCsepSelect = (csep) => {
        if (!csepModalTarget || !jhaData) return;

        const { value, isCustom } = csepModalTarget;
        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo }, stepThree: { ...jhaData.stepThree } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';

        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== value) return item;
            return {
                ...item,
                required: 'Yes Req',
                attachments: [], // either/or: linking a form clears any attached photo
                linkedCsepUuid: csep.uuid,
                linkedCsepData: {
                    uuid: csep.uuid,
                    location: csep.stepOne?.location || csep.stepTwo?.confinedSpaceLocation || '',
                    jobCode: csep.stepOne?.jobCode || '',
                    supervisor: csep.stepOne?.entrySupervisorName || '',
                    submittedAt: csep.submittedAt || csep.updatedAt
                }
            };
        });

        // Transfer hazards/energized sources from CSEP to JHA stepThree
        const csepEnergySources = csep.stepTwo?.energizedSources || [];
        if (csepEnergySources.length > 0) {
            const existingEnergyRows = updated.stepThree?.energyActivityRows || [];
            const newEnergyRows = [...existingEnergyRows];
            const csepWorkDescription = csep.stepTwo?.workDescription || '';

            csepEnergySources.forEach(source => {
                // Check if this energy source already exists in JHA
                const exists = existingEnergyRows.some(row =>
                    row.energySelections?.some(sel => sel.energyId === source.id || sel.energyId === source.ID)
                );

                // Only add if has hazards and doesn't exist
                if (!exists && source.hazards?.length > 0) {
                    newEnergyRows.push({
                        id: `csep-import-${Date.now()}-${source.id || source.ID}`,
                        workActivity: csepWorkDescription,
                        actions: '',
                        energySelections: [{
                            id: `csep-sel-${Date.now()}-${source.id || source.ID}`,
                            energyKey: source.id || source.ID,
                            energyId: source.id || source.ID,
                            energyName: source.value || source.name || '',
                            hazards: source.hazards || []
                        }]
                    });
                }
            });

            updated.stepThree.energyActivityRows = newEnergyRows;
            updated.stepThree.energyExposures = newEnergyRows;
        }

        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { minStep: 2 });
        setCsepModalOpen(false);
        setCsepModalTarget(null);
    };

    const handleCsepCreateNew = async () => {
        if (!jhaData) return;

        // Create new CSEP with pre-filled data from JHA
        const newCsep = createCsepSkeleton({
            stepOne: {
                mode: isLocationMode(jhaData.stepOne?.mode) ? 'location' : 'job',
                jobId: null,
                jobCode: jhaData.stepOne?.jobNumber || '',
                jobName: jhaData.stepOne?.jobSiteName || jhaData.stepOne?.projectName || '',
                location: jhaData.stepOne?.locationName || jhaData.stepOne?.jobSiteAddress || '',
                dateTime: localDateTimeString(), // local datetime (not UTC)
                entrySupervisorName: jhaData.stepOne?.jobSiteSupervisor || '',
                entrySupervisorType: 'employee'
            },
            stepTwo: {
                project: jhaData.stepOne?.projectName || jhaData.stepOne?.jobSiteName || '',
                confinedSpaceLocation: jhaData.stepOne?.locationName || jhaData.stepOne?.taskLocation || ''
            }
        });

        // Store JHA reference in CSEP for linking back
        newCsep.linkedJhaId = jhaData.id;

        // Save CSEP to IndexedDB
        try {
            const db = await initializeDB();
            const tx = db.transaction(CSEP_STORE, 'readwrite');
            await tx.objectStore(CSEP_STORE).put(newCsep);
            await tx.done;

            // Navigate to CSEP form
            setCsepModalOpen(false);
            setCsepModalTarget(null);
            navigate('/csep/step-1', {
                state: {
                    csep: newCsep,
                    isNew: false,
                    fromJha: true,
                    jhaId: jhaData.id
                }
            });
        } catch (e) {
            console.error('Error creating CSEP:', e);
        }
    };

    const handleUnlinkCsep = (value, isCustom) => {
        if (!jhaData || isReadOnly) return;

        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';

        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== value) return item;
            return {
                ...item,
                linkedCsepUuid: null,
                linkedCsepData: null
            };
        });

        scheduleSave(updated, { immediate: true, minStep: 2 });
    };

    // LTT Modal Handlers
    const handleLttSelect = (ltt) => {
        if (!lttModalTarget || !jhaData) return;

        const { value, isCustom } = lttModalTarget;
        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo }, stepThree: { ...jhaData.stepThree } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';

        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== value) return item;
            return {
                ...item,
                required: 'Yes Req',
                attachments: [], // either/or: linking a form clears any attached photo
                linkedLttUuid: ltt.uuid,
                linkedLttData: {
                    uuid: ltt.uuid,
                    location: ltt.header?.location || '',
                    jobCode: ltt.header?.jobCode || '',
                    jobName: ltt.header?.jobName || '',
                    workCarriedOutBy: ltt.header?.workCarriedOutBy || '',
                    submittedAt: ltt.meta?.submittedAt || ltt.updatedAt
                }
            };
        });

        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { minStep: 2 });
        setLttModalOpen(false);
        setLttModalTarget(null);
    };

    const handleLttCreateNew = async () => {
        if (!jhaData) return;

        // Create new LTT with pre-filled data from JHA
        const newLtt = createLttSkeleton({
            header: {
                mode: isLocationMode(jhaData.stepOne?.mode) ? 'location' : 'job',
                jobId: null,
                jobCode: jhaData.stepOne?.jobNumber || '',
                jobName: jhaData.stepOne?.jobSiteName || jhaData.stepOne?.projectName || '',
                location: jhaData.stepOne?.locationName || jhaData.stepOne?.jobSiteAddress || '',
                workCarriedOutBy: jhaData.stepOne?.jobSiteSupervisor || '',
                workCarriedOutByType: 'employee',
                descriptionOfWork: ''
            }
        });

        // Store JHA reference in LTT for linking back
        newLtt.linkedJhaId = jhaData.id;

        // Save LTT to IndexedDB
        try {
            const db = await initializeDB();
            const tx = db.transaction(LTT_STORE, 'readwrite');
            await tx.objectStore(LTT_STORE).put(newLtt);
            await tx.done;

            // Navigate to LTT form
            setLttModalOpen(false);
            setLttModalTarget(null);
            navigate('/ltt/form', {
                state: {
                    ltt: newLtt,
                    isNew: false,
                    fromJha: true,
                    jhaId: jhaData.id
                }
            });
        } catch (e) {
            console.error('Error creating LTT:', e);
        }
    };

    const handleUnlinkLtt = (value, isCustom) => {
        if (!jhaData || isReadOnly) return;

        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';

        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== value) return item;
            return {
                ...item,
                linkedLttUuid: null,
                linkedLttData: null
            };
        });

        scheduleSave(updated, { immediate: true, minStep: 2 });
    };

    // ─── FARP Modal Handlers ──────────────────────────────────────────
    // По аналогии с CSEP/LTT: либо линкуем уже поданный FARP по UUID,
    // либо создаём новый и переходим в FARP-форму.
    const handleFarpSelect = (farp) => {
        if (!farpModalTarget || !jhaData) return;

        const { value, isCustom } = farpModalTarget;
        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';

        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== value) return item;
            return {
                ...item,
                required: 'Yes Req',
                attachments: [], // either/or: linking a form clears any attached photo
                linkedFarpUuid: farp.uuid,
                linkedFarpData: {
                    uuid: farp.uuid,
                    jobCode: farp.jobCode || farp.stepOne?.jobCode || '',
                    planDate: farp.planDate || farp.stepOne?.planDate || '',
                    submitterName: farp.submitterName || farp.stepOne?.submitterName || '',
                    submittedAt: farp.submittedAt || farp.updatedAt,
                }
            };
        });

        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { immediate: true, minStep: 2 });
        setFarpModalOpen(false);
        setFarpModalTarget(null);
    };

    const handleFarpCreateNew = () => {
        if (!jhaData) return;
        // Открываем штатную форму FARP с pre-fill из JHA + returnTo обратно
        // на шаг Documents этого же JHA (теперь step-3).
        const params = new URLSearchParams();
        if (jhaData?.stepOne?.jobNumber) params.set('jobCode', jhaData.stepOne.jobNumber);
        if (jhaData?.stepOne?.date) params.set('planDate', jhaData.stepOne.date);
        if (jhaData?.localId) params.set('jhaLocalId', jhaData.localId);
        let jhaPath;
        if (jhaData?.id) {
            jhaPath = `/jha/step-3?id=${encodeURIComponent(jhaData.id)}`;
        } else {
            jhaPath = '/jha';
        }
        params.set('returnTo', jhaPath);
        setFarpModalOpen(false);
        setFarpModalTarget(null);
        navigate(`/safety/forms/fall-arrest-rescue-plan/form?${params.toString()}`);
    };

    const handleUnlinkFarp = (value, isCustom) => {
        if (!jhaData || isReadOnly) return;

        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';

        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== value) return item;
            return {
                ...item,
                linkedFarpUuid: null,
                linkedFarpData: null,
            };
        });

        scheduleSave(updated, { immediate: true, minStep: 2 });
    };

    const persistAttachment = (value, isCustom, attachment) => {
        if (!attachment || !jhaData) return;
        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';
        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== value) return item;
            const attachments = Array.isArray(item.attachments) ? item.attachments : [];
            return {
                ...item,
                attachments: [...attachments, attachment],
                // Either/or: attaching a photo clears any linked digital form, so a
                // required form is never satisfied two ways at once.
                linkedFarpUuid: null,
                linkedFarpData: null,
                linkedCsepUuid: null,
                linkedCsepData: null,
                linkedLttUuid: null,
                linkedLttData: null,
            };
        });
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        // immediate: иначе debounce(400ms) теряет вложение, если юзер сразу
        // переходит на след. шаг — cleanup на unmount отменяет отложенную запись
        // в IndexedDB (см. handleRemoveAttachment / link-handlers — там тоже immediate).
        scheduleSave(updated, { immediate: true, minStep: 2 });
    };

    const handleCameraCapture = payload => {
        if (!cameraTarget) return;
        const attachment = buildAttachmentPayload(payload);
        if (attachment) {
            persistAttachment(cameraTarget.value, cameraTarget.isCustom, attachment);
        }
        setCameraTarget(null);
    };

    const handleRemoveAttachment = (value, isCustom, attachmentId) => {
        if (!jhaData || isReadOnly) return;
        const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
        const key = isCustom ? 'customDocsPermits' : 'docsPermits';
        updated.stepTwo[key] = (updated.stepTwo[key] || []).map(item => {
            if (item.value !== value) return item;
            return {
                ...item,
                attachments: (item.attachments || []).filter(att => att.id !== attachmentId)
            };
        });
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { immediate: true, minStep: 2 });
    };

    const isDocComplete = doc => doc.required === 'Yes Req' || doc.required === 'Not Req';
    const docsComplete = docsWithCustom.every(isDocComplete);

    // Список auto-required документов БЕЗ attachments / linked CSEP / linked LTT
    // (синхронный fallback — используется для disable кнопки и моментального бейджа).
    const missingAutoRequired = useMemo(() => {
        return docsWithCustom
            .filter(d => d.__autoRequired && !hasAttachmentOrLink(d))
            .map(d => ({
                name: d.value,
                reason: describeMatch(d.__matchInfo),
            }));
    }, [docsWithCustom]);

    // Async-уточнение через offline IDB-чек: учитывает submitted FARP-форму,
    // которой нет в stepTwo.attachments но есть в локальном IDB store
    // (fallArrestRescuePlans). Заменяет missingAutoRequired когда готов.
    const [missingFromIdb, setMissingFromIdb] = useState(null);
    useEffect(() => {
        if (!jhaData) return;
        let cancelled = false;
        (async () => {
            try {
                const list = await findMissingDocumentsLocal(jhaData);
                if (!cancelled) {
                    // reason из оценщика приходит как "Triggered by — <...>"; в рендере
                    // диалога мы сами префиксим "Triggered by —", поэтому здесь убираем
                    // дублирующийся префикс (иначе "Triggered by — Triggered by — ...").
                    setMissingFromIdb(list.map(m => ({
                        name: m.document_name,
                        reason: (m.reason || '').replace(/^\s*Triggered by\s*[—–-]\s*/i, ''),
                    })));
                }
            } catch (e) {
                console.warn('[JhaStepTwo] findMissingDocumentsLocal failed', e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jhaData?.id, jhaData?.stepTwo, jhaData?.stepThree, triggerMatches]);

    // ─── Auto-link FARP к JHA ────────────────────────────────────────────
    // Если документ Fall Arrest Rescue Plan требуется (Yes Req), ещё не привязан
    // и без аттача — но на устройстве уже есть submitted FARP для этого JHA
    // (по jhaLocalId, иначе job+date) — привязываем автоматически (пишем
    // linkedFarpUuid в docs). Так юзеру не нужно открывать модалку вручную, а
    // на финальном submit документ зачтётся как выполненный.
    useEffect(() => {
        if (isReadOnly || !jhaData) return;
        const base = jhaData.stepTwo?.docsPermits || [];
        const custom = jhaData.stepTwo?.customDocsPermits || [];
        const needsLink = [...base, ...custom].some(d =>
            isFarpDoc(d) && d.required === 'Yes Req' && !d.linkedFarpUuid &&
            (!Array.isArray(d.attachments) || d.attachments.length === 0)
        );
        if (!needsLink) return;

        // Авто-привязку делаем СТРОГО по jhaLocalId — это гарантирует, что при
        // нескольких JHA за один день (один job+date) каждый JHA привяжет ТОЛЬКО
        // свой FARP, а не чужой. Без стабильного localId авто-привязку не делаем
        // (юзер может привязать вручную кнопкой FARP).
        if (!jhaData.localId) return;
        const criteria = { jhaLocalId: jhaData.localId };

        let cancelled = false;
        (async () => {
            try {
                const { exists, plan } = await fallArrestRescuePlanService.checkExisting(criteria);
                if (cancelled || !exists || !plan?.uuid) return;
                const linkData = {
                    uuid: plan.uuid,
                    jobCode: plan.jobCode || jhaData.stepOne?.jobNumber || '',
                    planDate: plan.planDate || jhaData.stepOne?.date || '',
                    submitterName: plan.submitterName || '',
                    submittedAt: plan.submittedAt || null,
                    auto: true,
                };
                const updated = { ...jhaData, stepTwo: { ...jhaData.stepTwo } };
                const linkInList = (list) => (list || []).map(item => {
                    if (!isFarpDoc(item) || item.linkedFarpUuid) return item;
                    return { ...item, required: 'Yes Req', linkedFarpUuid: plan.uuid, linkedFarpData: linkData };
                });
                updated.stepTwo.docsPermits = linkInList(updated.stepTwo.docsPermits);
                updated.stepTwo.customDocsPermits = linkInList(updated.stepTwo.customDocsPermits);
                if (updated.status === STATUS_NEW) updated.status = STATUS_PROCESS;
                scheduleSave(updated, { immediate: true, minStep: 3 });
            } catch (e) {
                console.warn('[JhaStepTwo] FARP auto-link failed', e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jhaData?.id, jhaData?.stepTwo, jhaData?.localId, isReadOnly]);

    // Финальный список: предпочитаем IDB-чек (точнее, учитывает submitted FARP),
    // fallback — синхронный missingAutoRequired (мгновенный, до завершения async).
    const missingList = missingFromIdb !== null ? missingFromIdb : missingAutoRequired;

    const handleNext = async () => {
        // Documents is now step 3. Block continue to Crew until docs satisfied AND
        // the Initial JHA has been sent (Send Initial JHA lives on this step).
        if (!isReadOnly && missingList.length > 0) {
            setMissingDialog({
                items: missingList,
                message: 'The Work Activities / Energy Sources / Hazards you selected made the document(s) below Required. Provide each one before continuing.',
            });
            return;
        }
        const updated = {
            ...jhaData,
            meta: {
                ...(jhaData?.meta || {}),
                lastStep: Math.max(jhaData?.meta?.lastStep || 1, 4)
            }
        };
        setJhaData(updated);
        pendingRecordRef.current = updated;
        await flushSave();
        navigate('/jha/step-4', { state: { jha: updated, isNew: false, viewMode } });
    };

    const handleBack = async () => {
        pendingRecordRef.current = jhaData;
        await flushSave();
        // Work Activities is now step 2 (steps swapped)
        navigate('/jha/step-2', { state: { jha: jhaData, isNew: false, viewMode } });
    };

    if (loading || !jhaData) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 60px)',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <JhaStepHeader
                currentStep={3}
                jhaData={jhaData}
                fromViewer={fromViewer}
            />

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1100,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        px: { xs: 0.75, md: 1.5 },
                        py: { xs: 0.75, md: 1.25 }
                    }}
                >
                    <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
                        {!isReadOnly && triggerMatches.length > 0 && (
                            <Alert
                                severity={missingList.length > 0 ? 'warning' : 'success'}
                                variant="outlined"
                                sx={{ '& .MuiAlert-message': { width: '100%' } }}
                            >
                                <AlertTitle sx={{ fontWeight: 600 }}>
                                    {missingList.length > 0
                                        ? `${missingList.length} document(s) auto-required — action needed`
                                        : `${triggerMatches.length} document(s) auto-required — all satisfied`}
                                </AlertTitle>
                                <Typography variant="body2" sx={{ mb: missingList.length > 0 ? 1 : 0 }}>
                                    Based on the Work Activities, Energy Sources, and/or Hazards you selected
                                    (Step 3), the following documents must be filled in before you can submit.
                                    Attach a photo of the completed form, or link the corresponding permit (CSEP/LTT)
                                    where applicable.
                                </Typography>
                                {missingList.length > 0 && (
                                    <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, mb: 0, mt: 0.5 }}>
                                        {missingList.map((m, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                                                    {m.name}
                                                </Typography>
                                                {m.reason && (
                                                    <Typography variant="caption" component="div" color="text.secondary">
                                                        Triggered by — {m.reason}
                                                    </Typography>
                                                )}
                                            </li>
                                        ))}
                                    </Stack>
                                )}
                            </Alert>
                        )}

                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TableContainer component={Card} elevation={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                <Table size="small" sx={{ flex: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Document / Permit</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Required</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Attachments</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {docsWithCustom.map(doc => {
                                    const attachments = doc.attachments || [];
                                    const canPreview = attachments.length > 0;
                                    // Триггер-обязательный документ удовлетворён, если есть
                                    // фото/файл ИЛИ привязана форма (CSEP/LTT/FARP). Тогда
                                    // бейдж зелёный «Required ✓», а не тревожный оранжевый.
                                    const autoReqSatisfied = doc.__autoRequired && hasAttachmentOrLink(doc);
                                    return (
                                        <TableRow
                                            key={doc.value}
                                            hover={canPreview}
                                            onClick={() => {
                                                if (canPreview) {
                                                    setPreviewDoc({ ...doc, attachments });
                                                }
                                            }}
                                            sx={{ cursor: canPreview ? 'pointer' : 'default' }}
                                        >
                                            <TableCell>
                                                <Stack spacing={0.5}>
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                        <DescriptionIcon fontSize="small" color={doc.isCustom ? 'secondary' : 'disabled'} />
                                                        <Typography variant="body2">{doc.value}</Typography>
                                                        {doc.isCustom && (
                                                            <Chip label="Custom" size="small" color="secondary" variant="outlined" />
                                                        )}
                                                        {doc.__autoRequired && (
                                                            <Chip
                                                                label={autoReqSatisfied ? 'Required ✓' : 'Auto-required'}
                                                                size="small"
                                                                color={autoReqSatisfied ? 'success' : 'warning'}
                                                                variant="filled"
                                                            />
                                                        )}
                                                    </Stack>
                                                    {doc.__autoRequired && doc.__matchInfo && (
                                                        <Typography variant="caption" color={autoReqSatisfied ? 'success.dark' : 'warning.dark'} sx={{ pl: 3.5 }}>
                                                            Triggered by — {describeMatch(doc.__matchInfo)}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    size="small"
                                                    variant={doc.required === 'Yes Req' ? 'contained' : 'outlined'}
                                                    color="success"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        const newValue = doc.required === 'Yes Req' ? 'Not Req' : 'Yes Req';
                                                        handleRequirementChange(doc.value, newValue, doc.isCustom);
                                                    }}
                                                    disabled={isReadOnly}
                                                >
                                                    Yes Req
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    flexWrap="wrap"
                                                    alignItems="flex-start"
                                                    justifyContent={attachments.length ? 'flex-start' : 'center'}
                                                >
                                                    {/* Show linked CSEP info for special CSEP type */}
                                                    {doc.specialType === 'csep' && doc.linkedCsepUuid ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                p: 1,
                                                                backgroundColor: '#e8f5e9',
                                                                borderRadius: 1,
                                                                border: '1px solid #a5d6a7'
                                                            }}
                                                        >
                                                            <LinkIcon color="success" fontSize="small" />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                                                    Linked CSEP
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {doc.linkedCsepData?.location || doc.linkedCsepData?.jobCode || 'View details'}
                                                                </Typography>
                                                            </Box>
                                                            <Tooltip title="View permit">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        handleViewLinkedForm('csep', doc.linkedCsepUuid);
                                                                    }}
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!isReadOnly && (
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        handleUnlinkCsep(doc.value, doc.isCustom);
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    ) : doc.specialType === 'ltt' && doc.linkedLttUuid ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                p: 1,
                                                                backgroundColor: '#fff3e0',
                                                                borderRadius: 1,
                                                                border: '1px solid #ffcc80'
                                                            }}
                                                        >
                                                            <LinkIcon color="warning" fontSize="small" />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                                                    Linked LTT Permit
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {doc.linkedLttData?.jobCode ? `${doc.linkedLttData.jobCode} - ${doc.linkedLttData.jobName || ''}` : doc.linkedLttData?.location || 'View details'}
                                                                </Typography>
                                                            </Box>
                                                            <Tooltip title="View permit">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        handleViewLinkedForm('ltt', doc.linkedLttUuid);
                                                                    }}
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!isReadOnly && (
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        handleUnlinkLtt(doc.value, doc.isCustom);
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    ) : isFarpDoc(doc) && doc.linkedFarpUuid ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                p: 1,
                                                                backgroundColor: '#fffbeb',
                                                                borderRadius: 1,
                                                                border: '1px solid #fcd34d'
                                                            }}
                                                        >
                                                            <LinkIcon color="warning" fontSize="small" />
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                                                    Linked Fall Arrest Rescue Plan
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {doc.linkedFarpData?.jobCode
                                                                        ? `Job ${doc.linkedFarpData.jobCode}${doc.linkedFarpData.planDate ? ' · ' + doc.linkedFarpData.planDate : ''}`
                                                                        : 'View details'}
                                                                    {doc.linkedFarpData?.submitterName ? ` · by ${doc.linkedFarpData.submitterName}` : ''}
                                                                </Typography>
                                                            </Box>
                                                            <Tooltip title="View rescue plan">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        handleViewLinkedForm('farp', doc.linkedFarpUuid);
                                                                    }}
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {!isReadOnly && (
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        handleUnlinkFarp(doc.value, doc.isCustom);
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    ) : attachments.length ? (
                                                        attachments.map(attachment => (
                                                            <Box
                                                                key={attachment.id}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    setPreviewDoc({ ...doc, attachment });
                                                                }}
                                                                sx={{
                                                                    width: 92,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src={attachment.dataUrl}
                                                                    alt={attachment.name}
                                                                    sx={{
                                                                        width: '100%',
                                                                        height: 72,
                                                                        borderRadius: 1,
                                                                        objectFit: 'cover',
                                                                        border: '1px solid #e0e0e0',
                                                                        backgroundColor: '#fafafa'
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        width: '100%',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {attachment.name}
                                                                </Typography>
                                                                {!isReadOnly && (
                                                                    <Button
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            handleRemoveAttachment(doc.value, doc.isCustom, attachment.id);
                                                                        }}
                                                                        sx={{
                                                                            textTransform: 'none',
                                                                            fontSize: '0.7rem',
                                                                            minWidth: 0,
                                                                            px: 1,
                                                                            py: 0.25
                                                                        }}
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        ))
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {doc.specialType === 'csep' ? 'No CSEP linked' : doc.specialType === 'ltt' ? 'No LTT linked' : 'No attachments'}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    {!isReadOnly && doc.required === 'Yes Req' && (() => {
                                                        // Two ways to satisfy a required item — BOTH always shown so the
                                                        // user can switch freely between them. Choosing one clears the
                                                        // other in data (see persistAttachment / handle*Select), so a
                                                        // document is never satisfied two ways at once.
                                                        //   • Photo  — hand-complete & photograph (camera)
                                                        //   • Form   — fill the digital form in the app (forms only)
                                                        const isForm = Boolean(docFormType(doc));
                                                        const isLinked = docIsLinked(doc);
                                                        const hasPhoto = Array.isArray(doc.attachments) && doc.attachments.length > 0;
                                                        return (
                                                        <>
                                                            <Tooltip title={hasPhoto ? "Retake / add photo" : "Take a photo of the completed document"}>
                                                                <Button
                                                                    size="small"
                                                                    variant={hasPhoto ? "contained" : "outlined"}
                                                                    color={hasPhoto ? "success" : "primary"}
                                                                    startIcon={<PhotoCameraIcon fontSize="small" />}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        setCameraTarget({ value: doc.value, isCustom: doc.isCustom });
                                                                    }}
                                                                    sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                                                                >
                                                                    Photo
                                                                </Button>
                                                            </Tooltip>
                                                            {/* Universal "Form" button for ALL fillable forms (FARP/CSEP/LTT).
                                                                Same look/label everywhere so it's clear; opens the matching
                                                                form to fill in-app or link an existing one. */}
                                                            {isForm && (
                                                                <Tooltip title={isLinked ? "Open / change the completed form" : "Fill the form in the app (or link an existing one)"}>
                                                                    <Button
                                                                        size="small"
                                                                        variant={isLinked ? "contained" : "outlined"}
                                                                        color={isLinked ? "success" : "primary"}
                                                                        startIcon={<DescriptionIcon fontSize="small" />}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            openFormForDoc(doc);
                                                                        }}
                                                                        sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                                                                    >
                                                                        {isLinked ? 'Form ✓' : 'Fill Form'}
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                        );
                                                    })()}
                                                    {doc.isCustom && !isReadOnly && !doc.__autoRequired && (
                                                        <Tooltip title="Remove custom document">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleRemoveCustomDoc(doc.value);
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {doc.isCustom && !isReadOnly && doc.__autoRequired && (
                                                        <Tooltip title="Auto-required by trigger — cannot remove. Resolve by attaching a file/photo or submitting the corresponding form.">
                                                            <span>
                                                                <IconButton size="small" disabled>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                            </TableContainer>

                            {!isReadOnly && (
                                <Card elevation={1}>
                                    <CardContent>
                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                            Add Custom Document / Permit
                                        </Typography>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={{ xs: 1, sm: 1.5, md: 2 }}
                                            alignItems={{ xs: 'stretch', sm: 'center' }}
                                            sx={{ flexWrap: { xs: 'nowrap', sm: 'nowrap' } }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <TextField
                                                    value={otherDoc}
                                                    onChange={event => setOtherDoc(event.target.value)}
                                                    placeholder="Enter document name"
                                                    size="small"
                                                    fullWidth
                                                />
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={handleAddCustomDoc}
                                                disabled={!otherDoc.trim()}
                                                sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 160, md: 180 } }}
                                            >
                                                Add Document
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )}
                        </Box>

                        {/* Pre-shift signature + Send Initial JHA — moved here from Work
                            Activities (steps swapped: Documents is now step 3, so the JHA is
                            signed and sent after documents are reviewed). */}
                        <JhaInitialSubmission
                            jhaData={jhaData}
                            scheduleSave={scheduleSave}
                            flushSave={flushSave}
                            viewMode={viewMode}
                            isReadOnly={isReadOnly}
                            isSubmitted={isSubmitted}
                        />
                    </Stack>
                </Box>
            </Box>

            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: '#ffffff',
                    borderTop: '1px solid #e0e0e0',
                    zIndex: 5
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={{ px: 0.75, py: 0.6 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                    >
                        Back to Activities
                    </Button>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        {!docsComplete && (
                            <Chip label="Please mark each document" color="warning" size="small" />
                        )}
                        {docsComplete && missingList.length > 0 && (
                            <Chip
                                label={`${missingList.length} required doc(s) missing file or form`}
                                color="warning"
                                size="small"
                            />
                        )}
                        {docsComplete && missingList.length === 0 && !initialSent && (
                            <Chip label="Send initial JHA to continue" color="warning" size="small" />
                        )}
                        <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleNext}
                            disabled={!docsComplete || !initialSent}
                        >
                            Continue to Crew Selection
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            <Dialog
                open={Boolean(previewDoc)}
                onClose={() => setPreviewDoc(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{previewDoc?.value || 'Attachments'}</DialogTitle>
                <DialogContent dividers sx={{ maxHeight: '65vh' }}>
                    {previewDoc ? (
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {previewDoc.attachment?.name || previewDoc.attachments?.[0]?.name || 'Preview'}
                            </Typography>
                            {previewDoc.attachment ? (
                                <Box
                                    component="img"
                                    src={previewDoc.attachment.dataUrl}
                                    alt={previewDoc.attachment.name}
                                    sx={{
                                        width: '100%',
                                        maxHeight: 360,
                                        objectFit: 'contain',
                                        borderRadius: 1,
                                        backgroundColor: '#fafafa',
                                        border: '1px solid #e0e0e0'
                                    }}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No attachments uploaded.
                                </Typography>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDoc(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <CameraCapture
                open={Boolean(cameraTarget)}
                onClose={() => setCameraTarget(null)}
                onCapture={handleCameraCapture}
            />

            <Dialog
                open={Boolean(missingDialog)}
                onClose={() => setMissingDialog(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Missing required documents</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {missingDialog?.message}
                    </Typography>
                    <Box sx={{ mb: 2, p: 1.25, backgroundColor: '#f1f5f9', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            For each document below, do one of these:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.25 }}>
                            📷 Tap <Box component="strong">Photo</Box> to attach a picture of the completed paper form, or
                        </Typography>
                        <Typography variant="body2">
                            📝 Tap <Box component="strong">Fill Form</Box> to complete it digitally in the app.
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Still missing:
                    </Typography>
                    <Stack spacing={1}>
                        {(missingDialog?.items || []).map((item, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    p: 1.25,
                                    border: '1px solid #fbbf24',
                                    backgroundColor: '#fef3c7',
                                    borderRadius: 1,
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {item.name}
                                </Typography>
                                {item.reason && (
                                    <Typography variant="caption" color="text.secondary">
                                        Triggered by — {item.reason}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMissingDialog(null)}>OK</Button>
                </DialogActions>
            </Dialog>

            <CsepSelectionModal
                open={csepModalOpen}
                onClose={() => {
                    setCsepModalOpen(false);
                    setCsepModalTarget(null);
                }}
                onSelectCsep={handleCsepSelect}
                onCreateNew={handleCsepCreateNew}
                jhaData={jhaData}
            />

            <LttSelectionModal
                open={lttModalOpen}
                onClose={() => {
                    setLttModalOpen(false);
                    setLttModalTarget(null);
                }}
                onSelectLtt={handleLttSelect}
                onCreateNew={handleLttCreateNew}
                jhaData={jhaData}
            />

            <FarpSelectionModal
                open={farpModalOpen}
                onClose={() => {
                    setFarpModalOpen(false);
                    setFarpModalTarget(null);
                }}
                onSelectFarp={handleFarpSelect}
                onCreateNew={handleFarpCreateNew}
                jhaData={jhaData}
            />

            {/* Individual linked-form PDF preview (FARP/CSEP/LTT viewed from the list). */}
            <PdfPreviewModal
                open={formPdfOpen}
                onClose={() => { setFormPdfOpen(false); setFormPdfBlob(null); }}
                pdfBlob={formPdfBlob}
                loading={formPdfLoading}
                filename="Document.pdf"
            />
        </Box>
    );
}
